import { EntryStatusEnum } from '@clubspark-node/clubspark-tennis-types';

import { getClientConfig } from 'config/clients';
import { venueProfiles } from 'mocks/demoTournaments/demoTournaments';
import { generateStubHydratedMatchUp } from 'mocks/utils';
import { type Format } from 'utils/dateTimeUtils';

import { DrawAdditionalPlayerInformation, DrawMatchDayInformation, type DrawMatchDayInformationForDate } from './types';

export interface PublicSettingsData {
  id: string;
  selectedInformation: DrawMatchDayInformation[];
  dates: string[];
}

export const getPlayerAdditionalData = (
  person: any,
  teams?: any[],
  extensions?: any[],
  additionalPlayerInfo?: DrawAdditionalPlayerInformation[],
) => {
  const additionalInfo = additionalPlayerInfo?.[0] ?? DrawAdditionalPlayerInformation.NONE;

  switch (additionalInfo) {
    case DrawAdditionalPlayerInformation.COUNTRY:
      return person?.addresses?.[0]?.countryCode ?? '';
    case DrawAdditionalPlayerInformation.SECTION:
      return (
        person?._ustaSection?.name ??
        person?.extensions?.find((ext: any) => ext.name === 'ustaSection')?.value?.name ??
        ''
      );
    case DrawAdditionalPlayerInformation.DISTRICT:
      return (
        person?._ustaDistrict?.name ??
        person?.extensions?.find((ext: any) => ext.name === 'ustaDistrict')?.value?.name ??
        ''
      );
    case DrawAdditionalPlayerInformation.TEAM:
      return teams?.[0]?.participantName ?? '';

    case DrawAdditionalPlayerInformation.CONFERENCE:
      return extensions?.find((ext: any) => ext.name === 'conference')?.value?.replace('_', ' ') ?? '';
    // TODO: Don't have data for Region yet
    case DrawAdditionalPlayerInformation.REGION:
    case DrawAdditionalPlayerInformation.NONE:
      return '';
    case DrawAdditionalPlayerInformation.CITY_STATE:
      return [person?.addresses?.[0]?.city, person?.addresses?.[0]?.state].filter(Boolean).join(', ');
  }
};

export const getScheduleFormat = (
  showDate: boolean,
  showTime: boolean,
  dateTimeFormat: Format,
  dateFormat: Format,
  timeFormat: Format,
) => {
  if (showDate && showTime) return dateTimeFormat;
  if (showDate) return dateFormat;
  if (showTime) return timeFormat;
  return null;
};

export const getPublicMatchDayInfo = (
  schedule: { scheduledDate: string; scheduledTime: any; venueId: any },
  matchDayInfo: DrawMatchDayInformationForDate[],
) => {
  const hasScheduledTime = !!schedule.scheduledTime;
  const dateTimeFormat = getClientConfig()?.formats?.dateTimeFormat ?? {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    minute: '2-digit',
    hour: 'numeric',
  };
  const dateFormat = getClientConfig()?.formats?.dateFormat ?? { weekday: 'short', month: 'short', day: 'numeric' };
  const timeFormat = getClientConfig()?.formats?.timeFormat ?? { timeStyle: 'short' };

  const matchDayInfoForDate = matchDayInfo?.find((mdi) => mdi.dates.includes(schedule?.scheduledDate));
  if (!matchDayInfoForDate) {
    // If nothing has been specifically set for this date, return defaults
    return {
      scheduleFormat: hasScheduledTime ? dateTimeFormat : dateFormat,
      venueId: schedule?.venueId,
    };
  }

  const selectedInfoForDate = matchDayInfoForDate?.selectedInformation ?? [];

  const [showDate, showTime, showFacility] = [
    selectedInfoForDate.includes(DrawMatchDayInformation.DATE),
    selectedInfoForDate.includes(DrawMatchDayInformation.TIME) && hasScheduledTime,
    selectedInfoForDate.includes(DrawMatchDayInformation.FACILITY),
  ];

  const scheduleFormat = getScheduleFormat(showDate, showTime, dateTimeFormat, dateFormat, timeFormat);
  return {
    scheduleFormat,
    venueId: showFacility ? schedule?.venueId : undefined,
  };
};
export const getConfigColorForAdornmentMark = (participantWtnData) => {
  const { confidence } = participantWtnData;

  const wtnBands = getClientConfig().scalingTypes.reduce((filtered, configItem) => {
    if (configItem[0] === 'WTN') {
      filtered.push(configItem[1]);
    }
    return filtered;
  }, []);

  return wtnBands?.[0]?.confidenceBands?.sort((a, b) => (a.min < b.min ? 1 : -1)).find((band) => band.min <= confidence)
    ?.color;
};

export const getMockedPreviewMatchUp = (mockedDate: string, selectedInformations: Array<DrawMatchDayInformation>) => {
  const generatedMatchUp = generateStubHydratedMatchUp(mockedDate, EntryStatusEnum.ALTERNATE);
  const isVenueChecked = selectedInformations?.includes(DrawMatchDayInformation.FACILITY);
  const isDateChecked = selectedInformations?.includes(DrawMatchDayInformation.DATE);
  const isTimeChecked = selectedInformations?.includes(DrawMatchDayInformation.TIME);

  const venueInfo = {
    venueAbbreviation: venueProfiles[0].venueAbbreviation,
    venueId: venueProfiles[0].venueId,
  };

  return {
    ...generatedMatchUp,
    schedule: {
      ...(isDateChecked || isTimeChecked
        ? {
            ...generatedMatchUp?.schedule,
            ...(isVenueChecked && venueInfo),
          }
        : { ...(isVenueChecked && venueInfo) }),
    },
  };
};
